import React from 'react';
import { CellContext } from '@tanstack/react-table';

import { AdaptiveTableCell } from '@components/common';
import Actions from '@components/common/Actions';

import { generateDurationString } from '@utils/date';

import { IActiveAlarmOran } from '@interfaces/alarms/alarm';
import { ITitle } from '@interfaces/common/common';

import { EVendorsValues, TVendorsValues } from '@enums/common';

import { columnHeaders } from './constants';

import { getAlarmAvailableActions } from './AlarmDetails/utils';

interface IGetAlarmColumns {
	openDetailsModal: () => void;
	setSelectedAlarmInformation: React.Dispatch<React.SetStateAction<IActiveAlarmOran | null>>;
	openClearAlarmModal: () => void;
	openAcknowledgeAlarmModal: () => void;
	vendor: TVendorsValues;
	isActiveTab: boolean;
	isOranCompliantView?: boolean;
	availableVendorsCount?: number;
}

export const getActiveAlarmsColumns = ({
	openDetailsModal,
	setSelectedAlarmInformation,
	openAcknowledgeAlarmModal,
	openClearAlarmModal,
	vendor,
	isActiveTab,
	isOranCompliantView = false,
	availableVendorsCount = 0,
}: IGetAlarmColumns) => ([
	{
		header: 'Alarm ID',
		accessorKey: columnHeaders.alarmId?.[vendor] || columnHeaders.alarmId.LIMENET,
		isHidden: !columnHeaders.alarmId?.[vendor],
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();
			const title = (vendor === EVendorsValues.oran || vendor === EVendorsValues.benetel) && value?.title
				? value.title
				: value;

			return (
				<AdaptiveTableCell
					isCopyable
					text={title}
				/>
			);
		},
	},
	{
		header: 'Alarm Code',
		accessorKey: columnHeaders.alarmCode?.[vendor] || columnHeaders.alarmCode.FLEXI_DAS,
		isHidden: !columnHeaders.alarmCode?.[vendor],
		cell: ({ getValue }: CellContext<any, any>) => {
			const alarmCode = getValue();

			return (
				<AdaptiveTableCell
					isCopyable
					text={alarmCode}
				/>
			);
		},
	},
	{
		header: 'Alarm Name',
		accessorKey: columnHeaders.alarmName?.[vendor] || columnHeaders.alarmName.LIMENET,
		isHidden: !columnHeaders.alarmName?.[vendor],
		cell: ({ getValue }: CellContext<any, any>) => {
			const name = getValue();

			return (
				<AdaptiveTableCell
					isCopyable
					text={name}
				/>
			);
		},
	},
	{
		header: 'Group',
		accessorKey: columnHeaders.groups?.[vendor] || columnHeaders.groups.LIMENET,
		isHidden: !columnHeaders.groups?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const value = getValue();
			const groups = Array.isArray(value)
				? value.map(({ title }: ITitle) => title).join(',')
				: value?.title || '';

			return (
				<AdaptiveTableCell text={groups} />
			);
		},
	},
	{
		header: 'Status',
		accessorKey: columnHeaders.status?.[vendor] || columnHeaders.status.AWTG_ORAN,
		isHidden: !columnHeaders.status?.[vendor] || isActiveTab,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const { title = '' } = getValue() || {};

			return (
				<AdaptiveTableCell text={title} />
			);
		},
	},
	{
		header: 'DAS Name',
		accessorKey: columnHeaders.alarmDasIndex?.[vendor] || columnHeaders.alarmDasIndex.FLEXI_DAS,
		isHidden: !columnHeaders.alarmDasIndex?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const dasName = getValue();

			return (
				<AdaptiveTableCell
					isCopyable
					text={dasName}
				/>
			);
		},
	},
	{
		header: 'Pathname',
		accessorKey: columnHeaders.alarmPathName?.[vendor] || columnHeaders.alarmPathName.FLEXI_DAS,
		isHidden: !columnHeaders.alarmPathName?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const pathName = getValue();

			return (
				<AdaptiveTableCell
					isCopyable
					text={pathName}
				/>
			);
		},
	},
	{
		header: 'Severity',
		accessorKey: columnHeaders.severity?.[vendor] || columnHeaders.severity.LIMENET,
		isHidden: !columnHeaders.severity?.[vendor],
		cell: ({ getValue }: CellContext<any, any>) => {
			const severity = getValue();

			return (
				<AdaptiveTableCell
					color={severity?.color}
					text={severity?.title}
				/>
			);
		},
	},
	{
		header: 'Applicability',
		accessorKey: columnHeaders.applicability?.[vendor] || columnHeaders.applicability.AWTG_ORAN,
		isHidden: !columnHeaders.applicability?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const { title = '' } = getValue() || {};

			return (
				<AdaptiveTableCell text={title} />
			);
		},
	},
	{
		header: 'Module',
		accessorKey: columnHeaders.moduleName?.[vendor] || columnHeaders.moduleName.AWTG_ORAN,
		isHidden: !columnHeaders.moduleName?.[vendor],
		cell: ({ getValue }: CellContext<any, any>) => {
			const { title = '' } = getValue() || {};

			return (
				<AdaptiveTableCell text={title} />
			);
		},
	},
	{
		header: 'Network element',
		accessorKey: columnHeaders.networkElementName?.[vendor] || columnHeaders.networkElementName.AWTG_ORAN,
		isHidden: !columnHeaders.networkElementName?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			return (
				<AdaptiveTableCell text={getValue()} />
			);
		},
	},
	{
		header: 'Middleware',
		accessorKey: columnHeaders.middlewareName?.[vendor] || columnHeaders.middlewareName.AWTG_ORAN,
		isHidden: !columnHeaders.middlewareName?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			return (
				<AdaptiveTableCell text={getValue()} />
			);
		},
	},
	{
		header: 'Vendor',
		accessorKey: 'vendor',
		isHidden: !isOranCompliantView || availableVendorsCount <= 1,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const { title } = getValue();

			return (
				<AdaptiveTableCell text={title} />
			);
		},
	},
	{
		header: 'Probable Cause',
		accessorKey: columnHeaders.probableCause?.[vendor] || columnHeaders.probableCause.AWTG_ORAN,
		isHidden: !columnHeaders.probableCause?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			return (
				<AdaptiveTableCell text={getValue()} />
			);
		},
	},
	{
		header: 'Site ID',
		accessorKey: columnHeaders.siteId?.[vendor] || columnHeaders.siteId.LIMENET,
		isHidden: !columnHeaders.siteId?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const siteId = getValue();

			return <AdaptiveTableCell text={siteId} />;
		},
	},
	{
		header: 'Flex-5G',
		accessorKey: columnHeaders.crowdcellSystemId?.[vendor] || columnHeaders.crowdcellSystemId.LIMENET,
		isHidden: !columnHeaders.crowdcellSystemId?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const crowdcellSystemId = getValue();

			return <AdaptiveTableCell text={crowdcellSystemId} />;
		},
	},
	{
		header: 'Description',
		accessorKey: columnHeaders.description?.[vendor] || columnHeaders.description.FLEXI_DAS,
		isHidden: !columnHeaders.description?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const alarmMessage = getValue();

			return (
				<AdaptiveTableCell
					isCopyable
					text={alarmMessage}
				/>
			);
		},
	},
	{
		header: 'Raised',
		accessorKey: columnHeaders.raisedAtText?.[vendor]
			|| columnHeaders.raisedAtText.LIMENET,
		isHidden: !columnHeaders.raisedAtText?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const raised = getValue();

			return <AdaptiveTableCell text={raised} />;
		},
	},
	{
		header: 'Duration',
		accessorKey: columnHeaders.duration?.[vendor] || columnHeaders.duration.LIMENET,
		isHidden: !columnHeaders.duration?.[vendor] || !isActiveTab,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const duration = getValue();
			const formattedDuration = generateDurationString(duration);

			return <AdaptiveTableCell text={formattedDuration} />;
		},
	},
	{
		header: 'Acknowledged',
		accessorKey: columnHeaders.isAcknowledged?.[vendor] || columnHeaders.isAcknowledged.AWTG_ORAN,
		isHidden: !columnHeaders.isAcknowledged?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const isAcknowledged = getValue();
			const data = isAcknowledged ? 'Yes' : 'No';

			return <AdaptiveTableCell text={data} />;
		},
	},
	{
		header: 'Cleared',
		accessorKey: columnHeaders.clearedAtText?.[vendor] || columnHeaders.clearedAtText.LIMENET,
		isHidden: !columnHeaders.clearedAtText?.[vendor] || isActiveTab,
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			return <AdaptiveTableCell text={getValue() || '-'} />;
		},
	},
	{
		header: 'Condition',
		accessorKey: columnHeaders.triggerCondition?.[vendor] || columnHeaders.triggerCondition.LIMENET,
		isHidden: !columnHeaders.triggerCondition?.[vendor],
		enableSorting: false,
		cell: ({ getValue }: CellContext<any, any>) => {
			const triggerCondition = getValue();

			return <AdaptiveTableCell text={triggerCondition} />;
		},
	},
	{
		header: 'Actions',
		accessorKey: 'actions',
		enableSorting: false,
		cell: ({ row }: CellContext<any, any>) => {
			const { isClear, isAcknowledge } = getAlarmAvailableActions({ vendor, alarm: row.original });

			const viewHandler = () => {
				setSelectedAlarmInformation(row.original);
				openDetailsModal();
			};

			const clearDataHandler = () => {
				setSelectedAlarmInformation(row.original);
				openClearAlarmModal();
			};

			const acknowledgeHandler = () => {
				setSelectedAlarmInformation(row.original);
				openAcknowledgeAlarmModal();
			};

			return (
				<Actions
					acknowledgeHandler={acknowledgeHandler}
					acknowledgeTooltip='Acknowledge alarm'
					clearDataHandler={clearDataHandler}
					clearDataTooltip='Clear alarm'
					gridNumberOfColumns={3}
					isAcknowledge={isAcknowledge}
					isClearData={isClear}
					isView
					viewHandler={viewHandler}
					viewTooltip='View details'
				/>
			);
		},
	},
]);

